import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { addDays, civilDateOf, localMidnightIso, todayIn } from '@frisk/shared';
import { PATHS } from '../../navigation';
import { memberColor, t } from '../../theme';
import { useStore, useWasteFeedIds } from '../../store';
import { Widget, WidgetEmpty } from './Widget';

/**
 * Today and tomorrow from the subscribed calendars.
 *
 * The calendar screen loads whichever range the user was browsing; this needs
 * its own fixed window, so it asks for one on mount rather than borrowing
 * whatever happens to be in the store.
 */
export function TodayWidget() {
  const events = useStore((s) => s.events);
  const members = useStore((s) => s.members);
  const loadEvents = useStore((s) => s.loadEvents);
  const wasteFeeds = useWasteFeedIds();
  const navigate = useNavigate();

  const today = todayIn(Intl.DateTimeFormat().resolvedOptions().timeZone);

  // The calendar screen loads whatever range the user was browsing, so this
  // asks for its own fortnight rather than borrowing what happens to be there.
  useEffect(() => {
    void loadEvents(localMidnightIso(today), localMidnightIso(addDays(today, 14)));
  }, [loadEvents, today]);

  const upcoming = events
    // Bin collections are not appointments and have their own tile.
    .filter((e) => !(e.feedId && wasteFeeds.has(e.feedId)))
    .filter((e) => civilDateOf(e.startsAt) >= today)
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt));

  // Three fits the shortest the tile ever gets; a clipped fourth entry reads
  // as broken, whereas a counted remainder reads as deliberate.
  const shown = upcoming.slice(0, 3);

  const timeOf = (iso: string, allDay: boolean) =>
    allDay ? 'ganztägig' : new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

  const dayOf = (iso: string) =>
    civilDateOf(iso) === today
      ? 'Heute'
      : new Date(iso).toLocaleDateString('de-DE', { weekday: 'short' });

  return (
    <Widget kicker="ALS NÄCHSTES" onTap={() => navigate(PATHS.cal)}>
      {upcoming.length === 0 ? (
        <WidgetEmpty>Keine Termine.</WidgetEmpty>
      ) : (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10, overflow: 'hidden' }}>
          {shown.map((e) => {
            const who = members.find((m) => m.id === e.memberId);
            return (
              <div key={e.id} style={{ display: 'flex', gap: 10, alignItems: 'baseline', minWidth: 0 }}>
                <div
                  style={{
                    width: 4,
                    alignSelf: 'stretch',
                    flex: 'none',
                    borderRadius: 999,
                    background: who ? memberColor(who.hue) : 'oklch(0.5 0.012 60)',
                  }}
                />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {e.title}
                  </div>
                  <div style={{ fontFamily: t.mono, fontSize: 12, color: t.inkFaint, marginTop: 2 }}>
                    {dayOf(e.startsAt)} · {timeOf(e.startsAt, e.allDay)}
                  </div>
                </div>
              </div>
            );
          })}
          {upcoming.length > shown.length && (
            <div style={{ fontSize: 15, color: t.inkFaint }}>+{upcoming.length - shown.length} weitere</div>
          )}
        </div>
      )}
    </Widget>
  );
}
